import { AppConfig, BracketField, BracketPhase } from './socket';
import { getDefaultTeamsForBracket, getDefaultTeamsForLeaderboard } from '../config/defaultTeams';

// Claves canónicas de localStorage para toda la app.
// Usar siempre estas constantes en vez de literales como 'appConfig' o 'neurolimpiadas_app_config'.
export const STORAGE_KEYS = {
  STATE: 'neurolimpiadas_app_state',
  CONFIG: 'neurolimpiadas_app_config',
  TEAM_TEMPLATE: 'neurolimpiadas_team_template',
  TIMER_STATE: 'neurolimpiadas_timer_state',
  TIMER_CONTROL: 'neurolimpiadas_timer_control',
  TIMER_MASTER: 'neurolimpiadas_timer_master',
  GLOBAL_TIMER: 'neurolimpiadas_global_timer',
  EVENT_PREFIX: 'neurolimpiadas_event_',
  BACKUP_PREFIX: 'neurolimpiadas_backup_'
} as const;

const hasLocalStorage = (): boolean => typeof window !== 'undefined' && !!window.localStorage;

export interface Team {
  name: string;
  color?: string;
  image?: string;
  phases: {
    [key: string]: number;
  };
  total: number;
}

export interface AppState {
  timer: {
    minutes: number;
    seconds: number;
    title: string;
    subtitle: string;
    isRunning: boolean;
    currentTime: number;
  };
  bracket: {
    title: string;
    teams: string[];
    tournamentType: 'single' | 'double';
    allowScores: boolean;
    fields?: BracketField[];
    phases?: BracketPhase[];
    currentPhase?: number;
  };  leaderboard: {
    title: string;
    maxVisiblePositions?: number;
    phaseNames: {
      [key: string]: string;
    };
    teams: Team[];  };
  carousel: {
    autoPlay: boolean;
    interval: number;
    mode: 'sequential' | 'random';
    transition: 'fade' | 'slide' | 'zoom';
    showControls: boolean;
    media: Array<{
      id: string;
      type: 'image' | 'video';
      url: string;
      duration?: number;
      name?: string;
      size?: number;
      uploaded?: boolean;
    }>;
  };
}

class LocalStorageStateManager {
  private readonly STATE_KEY = STORAGE_KEYS.STATE;
  private readonly CONFIG_KEY = STORAGE_KEYS.CONFIG;
  private readonly TEAM_TEMPLATE_KEY = STORAGE_KEYS.TEAM_TEMPLATE;
  
  private defaultState: AppState = {
    timer: {
      minutes: 15,
      seconds: 0,
      title: "Tiempo de Competencia",
      subtitle: "Neurociencia Aplicada",
      isRunning: false,
      currentTime: 15 * 60
    },    bracket: {
      title: "Clasificaciones Neurolimpiadas",
      teams: [
        "Equipo Alpha", "Equipo Beta", "Equipo Gamma", "Equipo Delta",
        "Equipo Epsilon", "Equipo Zeta", "Equipo Eta", "Equipo Theta"
      ],
      tournamentType: 'single',
      allowScores: false,
      fields: [],
      phases: [],
      currentPhase: 1
    },    leaderboard: {
      title: "Tabla de Posiciones",
      phaseNames: {
        phase1: "Relevos",
        phase2: "Aro Canario",
        phase3: "Cifras y Letras",
        phase4: "Figuras de Colores",
        phase5: "Pizarra"
      },
      teams: getDefaultTeamsForLeaderboard({
        phase1: "Relevos",
        phase2: "Aro Canario",
        phase3: "Cifras y Letras",
        phase4: "Figuras de Colores",
        phase5: "Pizarra"
      })
    },
    carousel: {
      autoPlay: true,
      interval: 5000,
      mode: 'sequential',
      transition: 'fade',
      showControls: true,
      media: [
        {
          id: "1",
          type: "image",
          url: "/images/slide1.jpg",
          duration: 5000,
          name: "Imagen de ejemplo 1"
        },
        {
          id: "2", 
          type: "image",
          url: "/images/slide2.jpg",
          duration: 5000,
          name: "Imagen de ejemplo 2"
        }
      ]
    }
  };

  private defaultConfig: AppConfig = {
    timer: {
      minutes: 5,
      seconds: 0,
      title: 'Tiempo Restante',
      subtitle: 'Neurolimpiadas 2025',
      categories: []
    },    bracket: {
      title: 'Bracket de Competición',
      teams: getDefaultTeamsForBracket(),
      tournamentType: 'single',
      allowScores: false,
      workingFields: [],
      displayFields: [],
      fields: [], // Mantenemos para compatibilidad
      phases: [],
      currentPhase: 'phase1',
      displayMode: 'working'
    },leaderboard: {
      title: 'Tabla de Clasificación',
      phaseNames: {
        phase1: 'Relevos',
        phase2: 'Aro Canario',
        phase3: 'Cifras y Letras',
        phase4: 'Figuras de Colores',
        phase5: 'Pizarra'
      },
      teams: getDefaultTeamsForLeaderboard({
        phase1: 'Relevos',
        phase2: 'Aro Canario',
        phase3: 'Cifras y Letras',
        phase4: 'Figuras de Colores',
        phase5: 'Pizarra'
      })
    },
    podium: {
      title: 'Podium de Ganadores',
      subtitle: 'Neurolimpiadas 2025',
      teams: {
        first: '',
        second: '',
        third: ''
      },
      teamImages: {},
      showPodium: false,
      currentSlide: 0
    },
    carousel: {
      autoPlay: true,
      interval: 5000,
      mode: 'sequential',
      transition: 'fade',
      showControls: true,
      media: []
    },
    pizarra: {
      title: 'Pizarras Neurolimpiadas 2025',
      subtitle: 'Equipos',
      pizarras: [
        { id: '1', teamName: 'Equipo 1', number: 1, score: 0, color: '#007bff' },
        { id: '2', teamName: 'Equipo 2', number: 2, score: 0, color: '#28a745' },
        { id: '3', teamName: 'Equipo 3', number: 3, score: 0, color: '#dc3545' },
        { id: '4', teamName: 'Equipo 4', number: 4, score: 0, color: '#ffc107' }
      ],
      showNumbers: true,
      showTeamNames: true,
      showScores: false
    }
  };

  // Deep-merge por sección: garantiza que las claves nuevas del schema
  // no falten cuando el usuario tiene datos guardados de una versión anterior.
  private mergeBySection<T extends Record<string, any>>(defaults: T, saved: Partial<T>): T {
    const result = { ...defaults } as T;
    for (const key of Object.keys(defaults) as Array<keyof T>) {
      const savedSection = saved?.[key];
      const defaultSection = defaults[key];
      if (savedSection && typeof savedSection === 'object' && !Array.isArray(savedSection) &&
          defaultSection && typeof defaultSection === 'object' && !Array.isArray(defaultSection)) {
        result[key] = { ...defaultSection, ...savedSection } as T[keyof T];
      } else if (savedSection !== undefined) {
        result[key] = savedSection as T[keyof T];
      }
    }
    return result;
  }

  // Obtener estado completo
  getState(): AppState {
    if (!hasLocalStorage()) return this.defaultState;
    try {
      const savedState = localStorage.getItem(this.STATE_KEY);
      if (savedState) {
        return this.mergeBySection(this.defaultState, JSON.parse(savedState));
      }
    } catch (error) {
      console.error('Error al cargar estado desde localStorage:', error);
    }
    return this.defaultState;
  }

  // Guardar estado completo
  setState(state: Partial<AppState>): void {
    if (!hasLocalStorage()) return;
    try {
      const currentState = this.getState();
      const newState = { ...currentState, ...state };
      localStorage.setItem(this.STATE_KEY, JSON.stringify(newState));
    } catch (error) {
      console.error('Error al guardar estado en localStorage:', error);
    }
  }

  // Actualizar sección específica del estado
  updateStateSection<K extends keyof AppState>(section: K, data: Partial<AppState[K]>): void {
    try {
      const currentState = this.getState();
      const updatedState = {
        ...currentState,
        [section]: { ...currentState[section], ...data }
      };
      this.setState(updatedState);
    } catch (error) {
      console.error(`Error al actualizar sección '${section}':`, error);
    }
  }

  // Obtener configuración
  getConfig(): AppConfig {
    if (!hasLocalStorage()) return this.defaultConfig;
    try {
      const savedConfig = localStorage.getItem(this.CONFIG_KEY);
      if (savedConfig) {
        return this.mergeBySection(this.defaultConfig, JSON.parse(savedConfig));
      }
    } catch (error) {
      console.error('Error al cargar configuración desde localStorage:', error);
    }
    return this.defaultConfig;
  }

  // Guardar configuración
  setConfig(config: Partial<AppConfig>): void {
    if (!hasLocalStorage()) return;
    try {
      const currentConfig = this.getConfig();
      const newConfig = { ...currentConfig, ...config };
      localStorage.setItem(this.CONFIG_KEY, JSON.stringify(newConfig));
    } catch (error) {
      console.error('Error al guardar configuración en localStorage:', error);
      throw error;
    }
  }

  // Actualizar sección específica de la configuración
  updateConfigSection<K extends keyof AppConfig>(section: K, data: Partial<AppConfig[K]>): void {
    try {
      const currentConfig = this.getConfig();
      const updatedConfig = {
        ...currentConfig,
        [section]: { ...currentConfig[section], ...data }
      };
      this.setConfig(updatedConfig);
    } catch (error) {
      console.error(`Error al actualizar configuración '${section}':`, error);
    }
  }

  // Limpiar todo el estado y configuración
  clear(): void {
    try {
      localStorage.removeItem(this.STATE_KEY);
      localStorage.removeItem(this.CONFIG_KEY);
      console.log('🗑️ Estado y configuración limpiados');
    } catch (error) {
      console.error('Error al limpiar localStorage:', error);
    }
  }
  // Resetear a valores por defecto
  reset(): void {
    try {
      this.setState(this.defaultState);
      this.setConfig(this.defaultConfig);
      console.log('🔄 Estado y configuración reseteados a valores por defecto');
    } catch (error) {
      console.error('Error al resetear localStorage:', error);
    }
  }

  // Obtener plantilla de equipos seleccionada
  getSelectedTeamTemplate(): string {
    if (!hasLocalStorage()) return 'tenerife-norte';
    try {
      const savedTemplate = localStorage.getItem(this.TEAM_TEMPLATE_KEY);
      return savedTemplate || 'tenerife-norte';
    } catch (error) {
      console.error('Error al cargar plantilla de equipos:', error);
      return 'tenerife-norte';
    }
  }

  // Guardar plantilla de equipos seleccionada
  setSelectedTeamTemplate(templateId: string): void {
    if (!hasLocalStorage()) return;
    try {
      localStorage.setItem(this.TEAM_TEMPLATE_KEY, templateId);
    } catch (error) {
      console.error('Error al guardar plantilla de equipos:', error);
    }
  }

  // Limpiar completamente todo el localStorage de la app
  clearAll(): void {
    try {
      // Limpiar todas las claves relacionadas con la app
      localStorage.removeItem(this.STATE_KEY);
      localStorage.removeItem(this.CONFIG_KEY);
      localStorage.removeItem(this.TEAM_TEMPLATE_KEY);
      
      // Limpiar todos los eventos pendientes y cualquier otra clave de neurolimpiadas
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('neurolimpiadas')) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // También limpiar IndexedDB si existe
      if ('indexedDB' in window) {
        try {
          indexedDB.deleteDatabase('neurolimpiadas_media');
        } catch (error) {
          console.warn('No se pudo limpiar IndexedDB:', error);
        }
      }
      
      console.log('🗑️ Todo el localStorage e IndexedDB de la aplicación ha sido limpiado');
    } catch (error) {
      console.error('Error al limpiar localStorage:', error);
    }
  }
}

export const localStorageManager = new LocalStorageStateManager();
